import { FieldsForm } from "@/types/fieldsFormSchema";
import {
  ExportInput,
  Integration,
  IntegrationConnectionData,
  LassoAuthenticationResponse,
  ProviderConfigurationMetadata,
  ProviderConnectionInfoFieldMapping,
} from "@/types/integrations";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { api } from "./api";
import lassoUrls from "./lasso-urls";

export function getIntegrationConfig() {
  return {
    allowedIntegrations: [
      "WebCRM",
      "HubSpot",
      "Pipedrive",
      "ZendeskSell",
      "Salesforce",
      "DynamicsCRM",
      "Herobase",
      "HeroFlows",
      "CustomAPI", // TODO: Re-add when ready
      "Adversus",
      "Monday",
      "ZohoCRM",
      "LeadDesk",
      "Dropbox",
      "OneDrive",
    ],
    design: {
      hubspot: {
        color: "#FF7A59",
      },
      pipedrive: {
        color: "#017737",
      },
      zendesksell: {
        color: "#00363D",
      },
      salesforce: {
        color: "#00A1E0",
      },
      dynamicscrm: {
        color: "#002050",
      },
      webcrm: {
        color: "#F0F0F0",
      },
      monday: {
        color: "#6161FF",
      },
      zohocrm: {
        color: "#006eb9",
      },
      dropbox: {
        color: "#006eb9",
      },
      onedrive: {
        color: "#094AB2",
      },
    },
  };
}

export function getStorageConfig() {
  return {
    allowedProviders: ["Dropbox", "OneDrive", "GoogleDrive"],
    design: {
      dropbox: {
        color: "#0061FF",
      },
      onedrive: {
        color: "#094AB2",
      },
      googledrive: {
        color: "#0F9D58",
      },
    },
  };
}

export async function configureOAuth(
  config: ProviderConfigurationMetadata,
  integration: Integration,
  responseChannelId: string,
  url?: string,
) {
  try {
    const response = await api
      .post(
        `organizations/me/integrations/${integration.uniqueName.toLowerCase()}/oauth/url`,
        {
          json: {
            apiUrl: config.authenticationType.toLowerCase().includes("url")
              ? url
              : undefined,
            authType: config.authenticationType,
            responseChannelId,
          },
        },
      )
      .then((resp) => resp.json<LassoAuthenticationResponse>());
    const popId = `sales-oauth-${integration.name}-window`;
    const popup = window.open(
      response.oAuthRedirectUrl,
      popId,
      "width=600,height=800",
    );
    window.addEventListener("message", (event) => {
      if (event.origin !== lassoUrls.getAppsUrl()) return;
      popup?.close();
      if (event.data !== "success") {
        Sentry.captureMessage(`OAuth failed: ${event.data}`, "error");
        throw new Error("OAuth failed");
      }
    });
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      toast.error("Pop-up vinduet kunne ikke åbnes. Prøv venligst igen.", {
        description: "Er din browser konfigureret til at tillade pop-ups?",
      });
      return;
    }
    return waitForPopup(popup);
  } catch (error) {
    console.error("Error fetching OAuth configuration:", error);
    toast.error(
      "Der opstod en fejl under hentning af OAuth-konfigurationen. Prøv venligst igen.",
      {
        description: "Er din URL til OAuth korrekt?",
      },
    );
  }
}

async function waitForPopup(popup: Window | null): Promise<void> {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (popup?.closed) {
        clearInterval(interval);
        resolve();
      }
    }, 1000);
  });
}

export async function testIntegrationConnection(
  integration: Integration,
  config?: IntegrationConnectionData,
  expectFailure = false,
  silent = false,
) {
  try {
    const response = await toast
      .promise(
        (config
          ? api.post(
              `organizations/me/integrations/${integration.uniqueName}/test`,
              {
                json: config,
              },
            )
          : api.get(
              `organizations/me/integrations/${integration.uniqueName}/test`,
            )
        )
          .json<{ success: boolean; message: string }>()
          .then((res) => {
            if (!res.success && !expectFailure) {
              throw new Error(res.message);
            }
            return res;
          }),
        {
          id: "test-integration-connection",
          loading: silent
            ? undefined
            : `Tester forbindelse til ${integration.name}...`,
          success: silent
            ? undefined
            : ({ success }) => {
                if (expectFailure) return;
                if (success)
                  return `Forbindelsen til ${integration.name} er testet og fungerer korrekt.`;
              },
          error:
            expectFailure || silent
              ? undefined
              : "Der opstod en fejl under test af forbindelsen. Prøv venligst igen.",
          finally: () => {
            if (expectFailure) toast.dismiss("test-integration-connection");
          },
        },
      )
      .unwrap();
    if (response.success) {
      return true;
    }
  } catch (error) {
    console.error("Error testing integration connection:", error);
  }
  return false;
}

export function findControllingField(
  exportInput: ExportInput,
  conditionProperty: keyof Pick<ExportInput, "showIfTrue" | "requiredIfTrue">,
) {
  return (i: ExportInput) => {
    if (!exportInput[conditionProperty]?.includes("="))
      return i.id === exportInput[conditionProperty];
    else if (exportInput[conditionProperty]?.includes("!=")) {
      const [fieldName, _] = exportInput[conditionProperty].split("!=");
      return i.id === fieldName;
    } else if (exportInput[conditionProperty]?.includes("=")) {
      const [fieldName, _] = exportInput[conditionProperty].split("=");
      return i.id === fieldName;
    }
    return false;
  };
}

export const formatExportInputs = (
  data: Record<string, unknown>,
): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(data)
      .filter(([_, value]) => !!value)
      .map(([key, value]) => [
        key,
        Array.isArray(value) ? value.join("||") : String(value),
      ]),
  );
};

export const mapExportInputs = (
  fieldMappings: FieldsForm["fieldMappings"],
  existingMappings?: ProviderConnectionInfoFieldMapping[] | undefined,
): ProviderConnectionInfoFieldMapping[] => {
  const exportMappings: ProviderConnectionInfoFieldMapping[] = [];
  Object.entries(fieldMappings).forEach(([_, mappingsByCompositeKey]) => {
    if (!mappingsByCompositeKey) return;
    Object.entries(mappingsByCompositeKey).forEach(([_, mappings]) => {
      if (!mappings) return;

      exportMappings.push(
        ...mappings
          .filter(
            (m): m is ProviderConnectionInfoFieldMapping =>
              m.from !== null && m.to !== null,
          )
          .map((mapping) => {
            const existingMapping = existingMappings?.find(
              (m) => m.from === mapping.from && m.to === mapping.to,
            );

            return {
              from: {
                ...mapping.from,
                transformers:
                  existingMapping?.from.transformers ??
                  mapping.from.transformers ??
                  null,
              },
              to: {
                ...mapping.to,
                transformers:
                  existingMapping?.to.transformers ??
                  mapping.to.transformers ??
                  null,
              },
              update: mapping.update ?? false,
              locked: mapping.locked ?? false,
            };
          }),
      );
    });
  });
  return exportMappings;
};
